import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { ReactiveFormsModule } from '@angular/forms'
import { NewsletterSubscriptionReqDto, pickProps, pickPropsAssertDefined } from '@pai/commons'

import { AnalyticsService } from 'app/core/analytics/analytics.service'
import { AnalyticsEvent } from 'app/core/analytics/analytics-event.enum'
import { CmpNewsletterSubscription } from 'app/core/cms/generated/api-types'
import { FormsApi } from 'app/core/forms-api'
import { CURRENT_LANG } from 'app/core/i18n/language.token'
import { LoggerService } from 'app/core/logger/logger.service'
import { ButtonComponent } from 'app/shared/button/button.component'
import { NewsletterSubscriptionForm } from 'app/shared/newsletter-subscription-form/newsletter-subscription.form'
import { NewsletterSubscriptionFormComponent } from 'app/shared/newsletter-subscription-form/newsletter-subscription-form.component'
import { TranslatePipe } from 'app/shared/translate.pipe'
import { action } from 'app/utils/action'
import { injectApprovalResource } from 'app/utils/inject-approval-resource.function'
import { filterIfTruthy } from 'app/utils/rxjs/filter-if-truthy.operator'

import { CmsBlock } from '../../cms-block'

@Component({
  selector: 'pai-cmp-newsletter-subscription',
  imports: [NewsletterSubscriptionFormComponent, ReactiveFormsModule, TranslatePipe, ButtonComponent],
  templateUrl: './cmp-newsletter-subscription.component.html',
  styleUrl: './cmp-newsletter-subscription.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CmpNewsletterSubscriptionComponent extends CmsBlock<CmpNewsletterSubscription> {
  readonly __typename = 'CmpNewsletterSubscription'

  readonly #currentLanguage = inject(CURRENT_LANG)
  readonly #analytics = inject(AnalyticsService)
  readonly #logger = inject(LoggerService).getInstance('CmpNewsletterSubscriptionComponent')

  protected readonly form = new NewsletterSubscriptionForm()
  protected readonly submitAction = action(inject(FormsApi).sendNewsletterSubscription)

  private readonly approval = injectApprovalResource()

  constructor() {
    super()
    toObservable(this.submitAction.result).pipe(filterIfTruthy()).subscribe(this.onFormResult)
    toObservable(this.submitAction.error).pipe(filterIfTruthy()).subscribe(this.onFormError)
  }

  protected onSubmit() {
    if (!this.form.valid || this.submitAction.isPending() || this.approval.isLoading()) {
      return
    }
    const payload: NewsletterSubscriptionReqDto = {
      language: this.#currentLanguage,
      ...pickPropsAssertDefined(this.form.value, 'type', 'givenName', 'familyName'),
      ...pickProps(this.form.value, ['address', 'postalCode', 'city', 'country', 'email', 'comment']),
      approval: this.approval.value(),
    }
    this.submitAction.exec(payload)
    this.#analytics.trackEvent(AnalyticsEvent.NEWSLETTER_SUBSCRIPTION_FORM_SUBMIT)
  }

  private readonly onFormResult = () => {
    this.form.reset()
  }
  private onFormError = (error: unknown) => {
    this.#logger.error('Form Submit failed', error)
  }
}
